import {reportOffer} from '../../../config/bite-plans.json';
import {api,ApiError} from './api';
import {prepareBiteCheckoutMeasurement,trackBiteEvent,flushBiteMeasurement} from './bite-measurement';
import {prepareBiteAdvertising} from './bite-advertising';
import {biteCampaignSource,withBiteCampaign} from './bite-campaign-source';

type CheckoutInput={ticker:string;plan:'card'|'monthly';action?:'report'|'card';loggedIn:boolean;placement?:string;expectedAmount?:number;offer?:'report_once';loginPath?:'/bite'|'/bite/pro'|'/bite/membership'|'/bite/me';flow?:string;resume?:boolean};
export const checkoutPlacement=(value:string|null)=>['report_growth','report_chart','report_end','report_offer','completion_paywall','pricing','member_offer','credit_wallet'].includes(value||'')?value!:'';
export function biteCheckoutLogin(input:CheckoutInput) {
  const params=new URLSearchParams({plan:input.plan,action:input.action||'report'});
  if(input.loginPath==='/bite')params.set('intro','1');
  if(input.expectedAmount!=null)params.set('amount',String(input.expectedAmount));
  if(input.offer)params.set('offer',input.offer);
  if(input.flow)params.set('flow',input.flow);
  const placement=checkoutPlacement(input.placement||null);
  if(placement)params.set('cta',placement);
  return withBiteCampaign(`/bite/login?next=${encodeURIComponent(`${input.ticker ? `/bite/${encodeURIComponent(input.ticker)}` : input.loginPath||'/bite/membership'}?${params}`)}`);
}

const inFlight=new Map<string,Promise<{checkoutRequired:boolean;url?:string}>>();
export function requestBiteCheckout(input:CheckoutInput) {
  const normalized={...input,flow:input.flow||crypto.randomUUID()};
  const key=JSON.stringify([normalized.flow,input.loggedIn,input.plan,input.expectedAmount]);
  const existing=inFlight.get(key);if(existing)return existing;
  const promise=performCheckout(normalized).finally(()=>inFlight.delete(key));
  inFlight.set(key,promise);return promise;
}
async function performCheckout(input:CheckoutInput) {
  const {ticker,plan,action='report'}=input,label=checkoutPlacement(input.placement||null);
  const eventDetails={ticker,plan,label,flow:input.flow,...(input.offer==='report_once'?{report_offer:reportOffer.id}:{})};
  if(!input.resume)trackBiteEvent('checkout_click',eventDetails);
  const login=()=>{trackBiteEvent('login_click',eventDetails);void flushBiteMeasurement(true);return {checkoutRequired:true,url:biteCheckoutLogin(input)};};
  if(!input.loggedIn)return login();
  if(input.resume)void import('./bite-login-measurement').then(m=>m.completeBiteLoginMeasurement()).catch(()=>{});
  // Attribution can fail or be blocked; it must not strand a purchase intent.
  const optional=async<T,>(work:Promise<T>,fallback:T)=>{
    let timer:ReturnType<typeof setTimeout>|undefined;
    try{return await Promise.race([work.catch(()=>fallback),new Promise<T>(resolve=>{timer=setTimeout(()=>resolve(fallback),1800);})]);}
    finally{clearTimeout(timer);}
  };
  const [,context]=await Promise.all([optional(prepareBiteAdvertising(),undefined),optional(prepareBiteCheckoutMeasurement(),null)]);
  trackBiteEvent('checkout_request',eventDetails);
  try {
    const result=await api<{checkoutRequired:boolean;url?:string}>('/api/bite/stripe/checkout',{
      method:'POST',body:{ticker,plan,action,...(input.expectedAmount!=null?{expectedAmount:input.expectedAmount}:{}),...(input.offer?{offer:input.offer}:{})},
      headers:{'x-bite-flow':JSON.stringify(eventDetails),...(context?{'x-bite-measurement':encodeURIComponent(JSON.stringify({...context,label}))}:{}),
        ...(biteCampaignSource().length?{'x-bite-campaign':biteCampaignSource().join(',')}:{})},
    });
    if(result.url)trackBiteEvent('checkout_redirect',eventDetails);
    return result;
  }catch(error){if(error instanceof ApiError&&error.status===401)return login();trackBiteEvent('checkout_error',eventDetails);throw error;}
}
